<template>
  <vddl-list
    :list="tlist"
    :allowed-types="allowedTypes"
    class="overlay_container"
  >
    <div
      v-if="showModal"
      class="overlay"
      :class="{ nooverflow: displayAttachment === true }"
      @mousedown="onMouseDown"
      @mouseup="onMouseUp"
      @drop.prevent
    >
      <div class="modal" @drop.stop>
        <image-container
          ref="img_container"
          :component="component"
          @display-attachment="displayAttachment = true"
        />
        <div class="input_container">
          <input
            ref="name"
            type="text"
            class="name"
            :spellcheck="false"
            :value="currentName"
            :disabled="!canEdit"
            tabindex="0"
            @input="onInput"
            @blur="onBlurName"
            @keyup.enter="onEnter"
            @keyup.esc.stop="onEsc"
          />
        </div>
        <attribute-list
          container-type="components"
          :container-id="component.id"
        />
      </div>
    </div>
  </vddl-list>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AttributeList from '@/components/all/Attributes/AttributeList.vue';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS, DRAG_TYPE } from '@/constants.js';
import ImageContainer from './ImageContainer.vue';
import iconMetadata from '../../../vuex/icon-metadata.js';

export default {
  components: {
    'image-container': ImageContainer,
    AttributeList,
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      displayAttachment: false,
      tlist: [],
      allowedTypes: [DRAG_TYPE.ATTRIBUTE_COMPONENT, DRAG_TYPE.ATTRIBUTE_ASSET],
      changedName: null,
      mousedownOnModal: false, // check to avoid closing the modal when dragging and releasing outside
    };
  },

  computed: {
    ...mapGetters('project/data/components', [
      'getNode',
      'getCoverId',
      'getCoverIcon',
      'getDefaultIcon',
    ]),
    ...mapGetters('project/config/components', ['getOpen']),
    ...mapGetters('application/appStates', ['getIsProjectLoaded']),
    ...mapGetters('project/data/assets', ['getFullImageUrl']),

    /**
     * Returns whether to show the modal
     * @return {Boolean}
     */
    showModal() {
      return this.getIsProjectLoaded && this.getOpen !== null;
    },

    /**
     * Returns the component object
     * @return {Object}
     */
    component() {
      return this.getNode(this.getOpen);
    },

    /**
     * Returns the component asset cover id
     * @return {String|null}
     */
    coverId() {
      return this.getCoverId(this.component.id);
    },

    /**
     * Returns the full image url
     * @return {String|null}
     */
    imageUrl() {
      return this.getFullImageUrl(this.coverId);
    },

    /**
     * Returns the current component name
     * @return {String}
     */
    currentName() {
      return this.changedName !== null ? this.changedName : this.component.name;
    },

    /**
     * Returns the cover icon (or the default icon)
     * @return {String}
     */
    icon() {
      return this.getCoverIcon(this.component.id) || this.getDefaultIcon;
    },

    /**
     * Returns the artist of the cover icon
     * @return {String}
     */
    artist() {
      return iconMetadata.getIconArtist(this.icon);
    },

    /**
     * Returns the url of the icon artist page
     * @return {String}
     */
    artistPage() {
      return iconMetadata.getIconArtistPage(this.icon);
    },

    /**
     * Returns the name of the cover icon
     * @return {String}
     */
    iconName() {
      return iconMetadata.getIconName(this.icon);
    },
  },

  /**
   * Vue created lifecycle hook
   */
  created() {
    // Save a reference to the element that had the focus
    this.previousActiveElement = document.activeElement;
    // Add a global keydown listener (for ESC)
    window.addEventListener('keydown', this.onKeyDown);
  },

  /**
   * Vue beforeUnmount lifecycle hook
   */
  beforeUnmount() {
    // Restore focus to the element that had it before the modal was opened
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }

    window.removeEventListener('keydown', this.onKeyDown);
  },

  methods: {
    ...mapActions('project/data/components', ['customUpdate']),
    ...mapActions('project/config/components', ['setOpen']),

    /**
     * Event handler for window keydown event
     * @param {KeyboardEvent} event
     */
    onKeyDown(event) {
      const { key } = event;
      switch (key) {
        case 'Escape':
          this.closeModal();
          break;
        default:
          break;
      }
    },

    /**
     * Event handler for mousedown on overlay (and children)
     * @param {MouseEvent} event
     */
    onMouseDown(event) {
      // In case the user did not mouse down on overlay
      if (!event.target.classList.contains('overlay')) {
        this.mousedownOnModal = true;
      }
    },

    /**
     * Event handler for mouseup on overlay (and children)
     * @param {MouseEvent} event
     */
    onMouseUp(event) {
      // In case the user mouse up on overlay
      if (event.target.classList.contains('overlay')) {
        // The mouse up did not occur on scrollbar or the mousedown was on the overlay
        if (
          event.offsetX < event.target.clientWidth &&
          !this.mousedownOnModal
        ) {
          this.closeModal();
        }
      }
      this.mousedownOnModal = false;
    },

    /**
     * Closes the modal and saves
     */
    closeModal() {
      this.changedName = null;
      const name = this.$refs.name.value;
      this.setName(name);
      // Close the component if the asset overlay is not open
      if (!this.$refs.img_container.isAssetOverlayOpen) {
        this.setOpen({ id: null, apply: true });
      }
    },

    /**
     * Event handler for blur event on name
     */
    onBlurName(event) {
      this.changedName = null;
      const name = event.target.value;
      this.setName(name);
    },

    /**
     * Event handler for input event on name
     */
    onInput(event) {
      this.changedName = event.target.value;
    },

    /**
     * Update the component name according to the given value (if it is not empty)
     */
    setName(name) {
      if (!this.component) return;
      if (!name) {
        name = 'Untitled component';
      }
      if (name === this.component.name) return;
      this.customUpdate({
        id: this.component.id,
        props: { name },
        apply: true,
      });
    },

    /**
     * Event handler: The user presses Esc while focus is on name field
     */
    onEsc(event) {
      event.target.blur();
    },

    /**
     * Event handler: The user presses Enter while focus is on the name field
     */
    onEnter(event) {
      event.target.blur();
    },
  },
};
</script>

<style scoped lang="scss">
.dragging-over * {
  pointer-events: none;
}
.overlay_container {
  background: rgba(0, 0, 0, 0.5);
}
.overlay_container {
  background: rgba(0, 0, 0, 0.5);
}
.overlay_container,
.overlay {
  position: absolute;
  overflow: auto;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 203;
  .drop {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
  }
}

.overlay.nooverflow {
  overflow: hidden;
}

.attachment-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  overflow: auto;
  z-index: 101;
  background: rgba(0, 0, 0, 0.5);
  /* display:flex; */
  /* align-items:center; */
  /* justify-content: center; */
  // cursor:pointer;

  .attachment-container {
    padding: 60px 40px 60px 40px;
    min-height: 100%;

    &.icon {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .icon-container {
      border: 1px solid #191d1e;
      max-width: 100%;
      background: #000;
      border-radius: 12px;
      margin: 0 auto 30px auto;
      .svg-icon-container {
        padding: 70px;
        // border: 4px solid rgba(255, 255, 255, 0.05);
      }
    }

    img {
      /* flex:0; */
      display: block;
      margin: 0 auto;
      border-radius: 6px;
      max-width: 100%;
      /* margin-bottom:60px; */
      cursor: default;
    }

    .icon_details {
      position: fixed;
      left: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      background: rgba(0, 0, 0, 0.75);
      padding: 0 20px;

      p {
        font-size: 14px;
        color: #bcbcbc;

        .name {
          // font-weight: 600;
          color: #fff;
        }

        a {
          color: #db841e;
          text-decoration: none;
        }
      }
    }
  }

  .download-container {
    position: fixed;
    bottom: 0;
    right: 0;

    .download {
      display: flex;
      justify-content: space-between;
      color: #fff;
      font-size: 16px;
      padding: 20px 40px 20px 0;
      text-align: right;
      text-decoration: none;

      &:hover {
        color: #db841e;
      }
      span {
        margin: 0 0 0 7px;
        font-size: 14px;
      }
    }
  }
}

.modal {
  width: 512px;
  max-width: 100%;
  background: #2c3233;
  color: #bcbcbc;
  padding: 0;
  border-radius: 10px;
  margin: 20px auto 40px auto;
}

@media screen and (max-width: 480px) {
  .modal {
    width: calc(100% - 20px);
    margin-left: 10px;
    margin-right: 10px;
    margin-bottom: 100px;
    left: 0;
  }
}

.modal .input_container {
  padding: 0 20px;
  background: #34494d;

  .name {
    text-align: center;
    padding: 14px 0 14px;
    font-size: 16px;
    font-weight: 600;
    color: #bcbcbc;
    background: none;
    border: none;
    font-family: 'Open sans';
    width: 100%;

    &:hover,
    &:focus {
      color: #ddd;
    }

    &:disabled {
      color: #bcbcbc !important;
    }
  }
}

.modal .sections {
  padding: 10px 0 20px 0;
  display: flex;
  flex-flow: row wrap;

  span {
    display: flex;
    flex-flow: row wrap;
    width: 100%;
  }
}

.modal .buttonarea {
  padding: 20px 20px 20px;
}
.modal .buttonarea button.addfield {
  padding: 14px 16px;
  border-radius: 3px;
  width: 100%;
  display: flex;
  align-items: center;
  border: none;
  color: #bcbcbc;
  // color: #db841e;
  // background: #343b3c;
  background: transparent;
  cursor: pointer;
  font-family: 'Open sans';
  font-weight: 600;
  font-size: 14px;
  text-align: left;
  opacity: 0.5;
  .svg-icon-container {
    margin: 0 10px 0 0;
  }
  &:hover {
    background: #383f40;
    opacity: 1;
  }
}
</style>

<style lang="scss">
// .flip-list-move {
//   transition: transform 0s;
// }

// .no-move {
//   transition: transform 0s;
// }
.ghost {
  opacity: 0.5;
}

.attachment-container {
  svg {
    filter: drop-shadow(0px 0px 30px rgba(0, 0, 0, 1));
  }
}
</style>
